import './override'

import { CssBaseline } from '@material-ui/core'
import { MuiThemeProvider } from '@material-ui/core/styles'
import { ErrorBoundary } from '@sentry/react'
import React from 'react'
import ReactDOM from 'react-dom'
import { Provider, useDispatch, useSelector } from 'react-redux'
import { BrowserRouter as Router, Switch, Redirect } from 'react-router-dom'

import Login from 'components/Auth/LoginPage'
import Signup from 'components/Auth/SignupPage'
import Loader from 'components/common/Loader'
import { ToastContainer } from 'components/common/ToastContainer'
import EnterRoomIdScreen from 'components/EnterRoomIdScreen'
import ErrorPage from 'components/ErrorPage'
import FeedbackNew from 'components/Feedback/FeedbackNew'
import { GetRoom } from 'components/GetRoom'
import NotFound from 'components/NotFound/NotFound'
import PWAInstallButton from 'components/PWAInstallButton'
import MathJaxComponent from 'components/Quiz/Revamp/MathJaxComponent'
import UI from 'components/UI'
import VideoApp from 'components/VideoApp'
import batteryStatus from 'helpers/battery'
import { isElectron } from 'helpers/electron'
import { isEmpty } from 'helpers/lodash'
import { isDevelopment, isFunction } from 'helpers/miscellaneous'
import { navigationRef } from 'helpers/navigation'
import { initiateThirdPartyScripts } from 'helpers/scripts'
import { accessTokenHelper } from 'helpers/token'
import { getUserTypeFromToken, isTokenExpired } from 'helpers/user'
import useDevHooks from 'hooks/useDevHooks'
import useHeapLogger from 'hooks/useHeapLogger'
import useWindowResize from 'hooks/useWindowResize'
import PrivateRoute from 'routes/Private'
import PublicRoute from 'routes/Public'
import store from 'store'
import {
    dispatchHandleDeepLinking,
    dispatchUpdateEnvironment,
} from 'store/dispatcher/App'
import { _environment } from 'store/selectors/App'
import { _user } from 'store/selectors/User'
import { checkLogin } from 'store/thunks/User'
import { deviceDetails, isDesktop } from 'utils/device'
import { logger } from 'utils/logger'
import initializeSentry from 'utils/sentry'
import { useTwilioLogger } from 'utils/twilioLogger.js'

import theme from './theme'

import 'index.css'
import 'emoji-mart/css/emoji-mart.css'

const { ipcRenderer } = isFunction(window.require) && window.require('electron')

const electron = isElectron()

const privateRoutes = [
    { path: '/feedback', exact: true, component: FeedbackNew },
    { path: '/:URLRoomName', exact: true, component: VideoApp },
]

const openRoutes = [
    { path: '/', exact: true, component: electron ? GetRoom : NotFound },
    { path: '/error', exact: true, component: ErrorPage },
    { path: '/signup', exact: true, component: Signup },
    { path: '/login', exact: true, component: Login },
    { path: '/join', exact: true, component: EnterRoomIdScreen },
]

const devRoutes = [{ path: '/ui', component: UI }]

logger.info(deviceDetails)

const Meets = () => {
    const environment = useSelector(_environment)
    const user = useSelector(_user)

    const dispatch = useDispatch()

    // handling deeplinking in case of electron app
    React.useEffect(() => {
        const handler = (event, data) => {
            console.log('deep linking data :- ', data)
            if (!isEmpty(data)) {
                if (data.includes('room-url=')) {
                    dispatchHandleDeepLinking(data)
                } else if (data.startsWith('/')) {
                    window.location.href = `${window.location.origin}${data}`
                }
            }
        }

        if (electron) {
            // listen and handle
            ipcRenderer.on('open-url', handler)
        }

        return () => {
            if (electron) {
                ipcRenderer.removeListener('open-url', handler)
            }
        }
    }, [])

    const [authenticated] = React.useMemo(() => {
        // check with state
        if (user?.authenticated && user?.type) {
            const { userInfo, type } = user
            const { user_id, phone_number, full_name } = userInfo

            if (!isEmpty(environment)) {
                /* 
                initializeSentry with user details after login
            */
                initializeSentry(
                    {
                        userId: user_id,
                        userType: type,
                        userName: full_name,
                        phoneNumber: phone_number,
                    },
                    environment
                )
            }

            return [user.authenticated]
        }

        // if not got proper authenticated value in store state , check token in storage once
        const accessToken = accessTokenHelper.get()
        if (accessToken) {
            /**
             * check with isTokenExpired,
             * if token is a valid JWT token , it will return true/false
             * otherwise undefined
             */
            const expired = isTokenExpired(accessToken || '')

            if (typeof expired === 'boolean' && !expired) {
                /**
                 * now as token is valid JWT token (may be expired)
                 * so now get user type from token
                 */
                const userType = getUserTypeFromToken(accessToken)
                return [!expired, userType]
            }
        }

        // return authenticated false with userType none
        return [false]
    }, [user, environment])

    React.useEffect(() => {
        if (!electron) {
            // update environment if this is a web app with CUSTOM_NODE_ENV configuration
            // as this is needed to work our cross platform
            const env = process.env.REACT_APP_CUSTOM_NODE_ENV

            if (env) {
                dispatchUpdateEnvironment(env)
            }
        }
    }, [])

    React.useEffect(() => {
        if (!isEmpty(environment)) {
            // initialize third party script
            initiateThirdPartyScripts()

            // initialize sentry
            initializeSentry({}, environment) // initializeSentry w/o user details before login

            // check for login
            dispatch(checkLogin({}))
        }
    }, [dispatch, environment])

    return (
        <Router ref={navigationRef}>
            <Switch>
                {openRoutes
                    .map(route => (
                        <PublicRoute
                            authenticated={authenticated}
                            key={`${route.path}`}
                            {...route}
                        />
                    ))
                    .concat(
                        isDevelopment
                            ? devRoutes.map(route => (
                                  <PublicRoute
                                      authenticated={authenticated}
                                      key={`${route.path}`}
                                      {...route}
                                  />
                              ))
                            : []
                    )
                    .concat(
                        privateRoutes.map(route => (
                            <PrivateRoute
                                authenticated={authenticated}
                                key={`${route.path}`}
                                {...route}
                            />
                        ))
                    )}
                <Redirect to="/" />
            </Switch>
        </Router>
    )
}

const getAppWrapper = () => {
    let App = () => {
        const processingDevHooks = useDevHooks()
        useWindowResize()
        useTwilioLogger()
        useHeapLogger()
        batteryStatus()

        if (processingDevHooks) return <Loader overlay />

        return (
            <>
                <Meets />
                {/* {!isDesktop && <PWAInstallButton />} */}
            </>
        )
    }

    return <App />
}

const renderOhApp = () => {
    ReactDOM.render(
        <ErrorBoundary fallback={<ErrorPage />}>
            <MuiThemeProvider theme={theme}>
                {/*Pre loading MathJax to avoid rendering issues in Quiz*/}
                <MathJaxComponent />
                <CssBaseline />
                <Provider store={store}>{getAppWrapper()}</Provider>
                <ToastContainer />
            </MuiThemeProvider>
        </ErrorBoundary>,
        document.getElementById('root')
    )
}

renderOhApp()

// Install ServiceWorker and AppCache in the end since
// it's not most important operation and if main code fails,
// we do not want it installed
/* if (process.env.NODE_ENV === 'production' && !isDesktop) {
    require('offline-plugin/runtime').install() // eslint-disable-line global-require
} */
